import React, { createContext, useContext, useState, ReactNode } from 'react';
import { TranscriberResponseDto } from '@shared/services/apiService/apiService';

interface DialogState {
  createOpen: boolean;
  deleteOpen: boolean;
  transcriber?: TranscriberResponseDto | undefined;
}

interface TranscriberContextProps {
  dialogState: DialogState;
  transcriber?: TranscriberResponseDto;
  openDialog: (dialog: keyof DialogState) => void;
  closeDialog: (dialog: keyof DialogState) => void;
  setTranscriber: (transcriber?: TranscriberResponseDto | undefined) => void;
  refreshTranscribers: () => void;
  refreshKey: number;
}

const TranscriberContext = createContext<TranscriberContextProps | undefined>(undefined);

export const useTranscriberContext = () => {
  const context = useContext(TranscriberContext);
  if (!context) {
    throw new Error('useTranscriberContext must be used within a TranscriberProvider');
  }
  return context;
};

export const TranscriberProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    createOpen: false,
    deleteOpen: false,
    transcriber: undefined,
  });
  const [transcriber, setTranscriber] = useState<TranscriberResponseDto | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const openDialog = (dialog: keyof DialogState) => {
    setDialogState((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof DialogState) => {
    setDialogState((prev) => ({ ...prev, [dialog]: false }));
    if (dialog === 'deleteOpen') setTranscriber(undefined);
  };

  const refreshTranscribers = () => setRefreshKey((k) => k + 1);

  return (
    <TranscriberContext.Provider
      value={{
        dialogState,
        transcriber,
        openDialog,
        closeDialog,
        setTranscriber,
        refreshTranscribers,
        refreshKey,
      }}>
      {children}
    </TranscriberContext.Provider>
  );
};
